// web/src/components/SiteCard.tsx — a customer's website (D05). Links to the single My Site screen.
// Shows the registry `stage` badge (portal spec 11: Live, Getting ready, …). Only an unsold `spec`
// site shows a Claim button (engaged owner sites auto-activate at build). Click is isolated from
// the card link.
import { Link } from 'react-router-dom';
import type { Site, SiteStage } from '../lib/types';
import { Icon } from './Icon';
import { FEATURES } from '../lib/features';

const STAGE: Record<SiteStage, { label: string; cls: string }> = {
  demo: { label: 'Your preview', cls: 'text-info' },
  sold: { label: 'Getting ready', cls: 'text-warn' },
  finishing: { label: 'Getting ready', cls: 'text-warn' },
  going_live: { label: 'Going live', cls: 'text-info' },
  live: { label: 'Live', cls: 'text-run' },
  paused: { label: 'Paused', cls: 'text-danger' },
  offboarding: { label: 'Ending', cls: 'text-warn' },
  offboarded: { label: 'Ended', cls: 'text-muted' },
  archived: { label: 'Archived', cls: 'text-muted' },
  legacy: { label: 'Live', cls: 'text-run' },
};

export function SiteCard({ site, onClaim, claiming }: {
  site: Site; onClaim?(id: string): void; claiming?: boolean;
}) {
  const s = STAGE[site.stage] ?? STAGE.legacy;
  return (
    <Link to={`/site?id=${site.id}`}
      className="block rounded-lg border border-hair bg-surface p-4 transition-transform hover:border-surface2 active:scale-[0.99]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="world" size={16} className="text-info" />
            <span className="truncate font-medium text-ink">{site.name}</span>
          </div>
          {site.url && <div className="mt-0.5 truncate text-xs text-muted">{site.url.replace(/^https?:\/\//, '')}</div>}
        </div>
        <Icon name="chevron-right" size={18} className="mt-0.5 shrink-0 text-muted" />
      </div>
      <div className="mt-3 flex items-center gap-4 text-xs text-muted">
        <span className={`font-head ${s.cls}`}>{s.label}</span>
        <span>{site.openRequests} open {site.openRequests === 1 ? 'request' : 'requests'}</span>
        {site.lastRelease && <span className="font-head text-run">{site.lastRelease.version}</span>}
      </div>
      {FEATURES.builds && site.state === 'spec' && onClaim && ( /* hidden with builds (portal spec 10) */
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClaim(site.id); }}
          disabled={claiming}
          className="mt-3 inline-flex min-h-9 items-center gap-2 rounded-md bg-run px-3 text-xs font-medium text-runink transition-transform active:scale-[0.98] disabled:opacity-50">
          <Icon name="check" size={14} />{claiming ? 'Working…' : 'Claim this site'}
        </button>
      )}
    </Link>
  );
}
